// Content for the Compteurs d'eau ATNA « Le défi » section.
// Dashboard and card values are illustrative demo data, shown only inside the product visual.
export type Bi = { fr: string; en: string }

export const WATER_CHALLENGE_IMAGES = {
  aerial: 'https://cdn.magicpatterns.com/patterns/generated-images/2c1628ce-ef84-49fc-a14c-98bf20ad63b2.jpg',
  sprinkler: 'https://cdn.magicpatterns.com/patterns/generated-images/fbdbf359-267a-4b23-91cc-9c97baca67b1.jpg',
  night: 'https://cdn.magicpatterns.com/patterns/generated-images/c0ad0471-c2ee-41b7-bd62-1d705323dacb.jpg',
}

export const waterChallengeCopy = {
  eyebrow: { fr: 'Le défi', en: 'The challenge' },
  navy: [
    { fr: 'Beaucoup de données.', en: 'Plenty of data.' },
    { fr: 'Pas assez de visibilité.', en: 'Not enough visibility.' },
  ],
  indigo: { fr: 'Trop peu d’actions.', en: 'Too little action.' },
  body: [
    { fr: 'Les compteurs génèrent des données en continu.', en: 'Meters generate data continuously.' },
    {
      fr: 'Mais sans intelligence pour les interpréter, il reste difficile de repérer rapidement les surconsommations, les fuites et les comportements inhabituels — et d’informer les bonnes personnes au bon moment.',
      en: 'But without intelligence to interpret it, it remains hard to quickly spot overconsumption, leaks and unusual patterns — and to inform the right people at the right time.',
    },
  ],
  aerialAlt: { fr: 'Vue aérienne d’une municipalité riveraine', en: 'Aerial view of a riverside municipality' },
}

export type InsightTone = 'red' | 'orange' | 'indigo'
export type InsightKey = 'high' | 'irrigation' | 'leak' | 'peak'

export const waterInsights: { key: InsightKey; tone: InsightTone; title: Bi; lines: Bi[] }[] = [
  { key: 'high', tone: 'red', title: { fr: 'Consommation élevée', en: 'High consumption' }, lines: [{ fr: '127 propriétés', en: '127 properties' }, { fr: '+32 % par rapport à la moyenne', en: '+32% vs average' }] },
  { key: 'irrigation', tone: 'orange', title: { fr: 'Arrosage prolongé', en: 'Prolonged watering' }, lines: [{ fr: 'Détecté cette semaine', en: 'Detected this week' }] },
  { key: 'leak', tone: 'red', title: { fr: 'Fuite potentielle', en: 'Potential leak' }, lines: [{ fr: '18 consommations', en: '18 continuous' }, { fr: 'continues la nuit', en: 'night-time readings' }] },
  { key: 'peak', tone: 'indigo', title: { fr: 'Pointe de consommation', en: 'Consumption peak' }, lines: [{ fr: '+14 % cette semaine', en: '+14% this week' }] },
]

export const challengeDashboard = {
  search: { fr: 'Rechercher une adresse, un compteur, un secteur…', en: 'Search by address, meter or sector…' },
  org: { fr: 'Municipalité de Casselman', en: 'Municipality of Casselman' },
  nav: [
    { key: 'overview', label: { fr: 'Aperçu', en: 'Overview' } },
    { key: 'properties', label: { fr: 'Propriétés', en: 'Properties' } },
    { key: 'meters', label: { fr: 'Compteurs', en: 'Meters' } },
    { key: 'consumption', label: { fr: 'Consommation', en: 'Consumption' } },
    { key: 'alerts', label: { fr: 'Alertes', en: 'Alerts' } },
    { key: 'interventions', label: { fr: 'Interventions', en: 'Work orders' } },
    { key: 'reports', label: { fr: 'Rapports', en: 'Reports' } },
  ] as { key: 'overview' | 'properties' | 'meters' | 'consumption' | 'alerts' | 'interventions' | 'reports'; label: Bi }[],
  kpis: [
    { key: 'high', value: '127', badge: { fr: '+32 %', en: '+32%' }, label: { fr: 'Consommations élevées', en: 'High consumption' }, note: null },
    { key: 'leak', value: '18', badge: null, label: { fr: 'Fuites potentielles', en: 'Potential leaks' }, note: null },
    { key: 'irrigation', value: '56', badge: null, label: { fr: 'Arrosages prolongés', en: 'Prolonged watering' }, note: null },
    { key: 'network', value: '+14 %', badge: null, label: { fr: 'Consommation du réseau', en: 'Network consumption' }, note: { fr: 'par rapport à la semaine précédente', en: 'vs previous week' } },
  ] as { key: 'high' | 'leak' | 'irrigation' | 'network'; value: string; badge: Bi | null; label: Bi; note: Bi | null }[],
  chartTitle: { fr: 'Consommation du territoire', en: 'Town-wide consumption' },
  days: { fr: ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'], en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] },
  values: [48, 72, 80, 84, 96, 90, 110],
  mapTitle: { fr: 'Carte des alertes', en: 'Alert map' },
  // x / y in % of the map panel
  markers: [
    { x: 55, y: 22, tone: 'orange' },
    { x: 73, y: 18, tone: 'red' },
    { x: 20, y: 48, tone: 'orange' },
    { x: 36, y: 55, tone: 'orange' },
    { x: 52, y: 54, tone: 'red' },
    { x: 66, y: 52, tone: 'orange' },
    { x: 48, y: 78, tone: 'orange' },
    { x: 62, y: 82, tone: 'orange' },
    { x: 12, y: 86, tone: 'orange' },
    { x: 58, y: 64, tone: 'red' },
  ] as { x: number; y: number; tone: 'orange' | 'red' }[],
}

export const waterProblems: { key: 'over' | 'leak' | 'drought' | 'reactive'; title: Bi; text: Bi }[] = [
  { key: 'over', title: { fr: 'Surconsommation invisible', en: 'Invisible overconsumption' }, text: { fr: 'Des hausses passent souvent inaperçues.', en: 'Increases often go unnoticed.' } },
  { key: 'leak', title: { fr: 'Fuites tardivement détectées', en: 'Leaks detected late' }, text: { fr: 'La consommation anormale peut durer des jours.', en: 'Abnormal consumption can last for days.' } },
  { key: 'drought', title: { fr: 'Pression en période de sécheresse', en: 'Drought pressure' }, text: { fr: 'Arrosage excessif et usages non essentiels.', en: 'Excessive watering and non-essential use.' } },
  { key: 'reactive', title: { fr: 'Interventions réactives', en: 'Reactive response' }, text: { fr: 'Des coûts plus élevés et une mobilisation accrue.', en: 'Higher costs and more crews mobilized.' } },
]
